import React, { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';

/** Exact text the admin must type (case-sensitive) to unlock the confirm button. */
const CONFIRM_TEXT = 'confirm';

/** Props for {@link ConfirmOldEditDialog}. */
interface ConfirmOldEditDialogProps {
    /** Whether the dialog is open. */
    open: boolean;
    /** The date (YYYY-MM-DD) of the entry being edited, shown in the prompt. */
    workDate: string;
    /** Called when the admin cancels or dismisses the dialog. */
    onCancel: () => void;
    /** Called once the admin has typed the exact confirmation text and clicks Confirm. */
    onConfirm: () => void;
    /**
     * Dialog title. Defaults to the edit-a-cell wording; pass a different
     * title (e.g. "Confirm reset of an older entry") when this dialog gates
     * a different action, such as resetting an admin override.
     */
    title?: string;
    /**
     * Noun phrase describing the action being confirmed, used in the prompt
     * body ("...to proceed with {actionDescription}"). Defaults to `'this
     * edit'`.
     */
    actionDescription?: string;
    /** Label for the confirm button. Defaults to `'Save'`. */
    confirmLabel?: string;
}

/**
 * ConfirmOldEditDialog – blocking confirmation prompt shown when an HR admin
 * changes a timesheet cell whose date is more than 7 days in the past. Used
 * both when saving a new code and when resetting a saved override back to
 * its generated default (see {@link title}/{@link actionDescription}/
 * {@link confirmLabel}).
 *
 * The admin must type `confirm` (exact, case-sensitive) into the text field
 * before the Confirm button becomes enabled. This mirrors the backend's
 * required `confirmation: "confirm"` field, which re-validates independently
 * of this UI gate.
 *
 * @param props - See {@link ConfirmOldEditDialogProps}.
 */
export const ConfirmOldEditDialog: React.FC<ConfirmOldEditDialogProps> = ({
    open,
    workDate,
    onCancel,
    onConfirm,
    title = 'Confirm edit to an older entry',
    actionDescription = 'this edit',
    confirmLabel = 'Save',
}) => {
    const [typed, setTyped] = useState('');

    useEffect(() => {
        if (open) setTyped('');
    }, [open]);

    const isMatch = typed === CONFIRM_TEXT;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    The date {workDate} is more than 7 days in the past. Type <strong>confirm</strong> below
                    to proceed with {actionDescription}.
                </DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    variant="standard"
                    label="Type &quot;confirm&quot;"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    inputProps={{ 'aria-label': 'confirmation text' }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button onClick={onConfirm} disabled={!isMatch} variant="contained" color="primary">
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
